import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useTimer } from "./useTimer";
import { useLangContext } from "../providers/LangProvider/LangProviderContext";
import _ from "lodash";

const formatTwoDigit = (digit: number): string => digit < 10 ? `0${digit}` : `${digit}`;

interface IUseSecondsCountdownProps {
    endDate?: string | null,
    initialSecondsLeft?: number,
    formatLabelParams?: {
        days?: boolean,
        hours?: boolean,
        minutes?: boolean,
        seconds?: boolean,
    },
}

function useSecondsCountdown(props: IUseSecondsCountdownProps): {
    isInited: boolean,
    formattedLabel: string,
    secondsLeft: number,
} {
    const { endDate, initialSecondsLeft, formatLabelParams } = props;
    const { getLangPath } = useLangContext();
    const [secondsLeft, setSecondsLeft] = useState(null);

    const init = useCallback(() => {
        if (!endDate && initialSecondsLeft === undefined) {
            return;
        }
        let secondsLeft: number;
        if (initialSecondsLeft !== undefined) {
            secondsLeft = initialSecondsLeft;
        } else {
            const now = moment();
            const endAtMoment = moment(endDate);
            const diff = endAtMoment.diff(now, 'seconds');
            secondsLeft = diff > 0 ? diff : 0;
        }
        setSecondsLeft(secondsLeft);
    }, [endDate, initialSecondsLeft]);

    const handleUpdateSecondsLeft = useCallback(() => {
        setSecondsLeft((secondsLeft: number) => secondsLeft - 1);
    }, []);

    const { isTimerRunning, startTimer, stopTimer } = useTimer(handleUpdateSecondsLeft);

    useEffect(() => {
        init();
    }, [init]);

    useEffect(() => {
        const isNeedTimer = secondsLeft > 0;
        if (isNeedTimer && !isTimerRunning) {
            startTimer(1000);
        }
        if (!isNeedTimer && isTimerRunning) {
            stopTimer();
        }
    }, [secondsLeft, isTimerRunning, startTimer, stopTimer]);

    const formattedLabel = useMemo(() => {
        if (_.isEmpty(formatLabelParams)) {
            return `${secondsLeft}`;
        }
        const params: string[] = [];
        const duration = moment.duration(secondsLeft, 'seconds');
        if (formatLabelParams.days) params.push(`${duration.days()}${getLangPath('format.days_name.short')}`);
        if (formatLabelParams.hours) params.push(`${formatTwoDigit(duration.hours())}${getLangPath('format.hours_name.short')}`);
        if (formatLabelParams.minutes) params.push(`${formatTwoDigit(duration.minutes())}${getLangPath('format.minutes_name.short')}`);
        if (formatLabelParams.seconds) params.push(`${formatTwoDigit(duration.seconds())}${getLangPath('format.seconds_name.short')}`);
        return params.join(' ');
    }, [formatLabelParams, getLangPath, secondsLeft]);

    return {
        isInited: secondsLeft !== null,
        formattedLabel,
        secondsLeft: secondsLeft || 0,
    }
}

export default useSecondsCountdown;
